import { useEffect, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import { useSortingContext } from 'utils/contexts/sorting';
import { STANDARD_TIME } from 'constants/animations';

import { useSnippetSearch } from './../hooks/useSnippetSearch';
import { SnippetUsage, InactiveState } from './../types';
import { useSnippetReferencesFilter } from '../context/SnippetReferencesFilterContext';
import { useReferencePaginationContext } from '../context/ReferencesPaginationContext';

import {
  handleReferenceSort,
  splitArrayIntoSubArrays,
  getRowsToDisplay,
  addResultTypeToRow,
  updateRowNames,
} from '../utils';

type ColumnTypes = {
  [key: string]: string;
};

const ReferenceHeaders: ColumnTypes = {
  name: 'name',
  messageMedium: 'messageMedium',
  resultType: 'resultType',
  campaignState: 'campaignState',
  creationDate: 'creationDate',
  updatedAt: 'updatedAt',
};

type Variables = {
  snippetName: string;
};

export function useSnippetReferences({ snippetName }: Variables) {
  const { results, isLoading } = useSnippetSearch({ name: snippetName });

  const { sortBy, sortDirection } = useSortingContext();
  const { selectedFilters } = useSnippetReferencesFilter();
  const { limit, currentPage } = useReferencePaginationContext();

  const [isUpdating, setIsUpdating] = useState(true);

  // All rows with the included 'resultType' key
  const [allReferences, setAllReferences] = useState<SnippetUsage[]>([]);
  // The current rows displaying on a page
  const [referencesByPage, setReferencesByPage] = useState<SnippetUsage[]>([]);
  // The total amount of rows after filtering
  const [activeTotal, setActiveTotal] = useState<SnippetUsage[]>([]);

  const handleRowUpdates = (arr: SnippetUsage[]) => {
    let newArr = [...arr];

    if (ReferenceHeaders[sortBy]) {
      newArr = handleReferenceSort(arr, sortBy, sortDirection);
    }

    const pages: SnippetUsage[][] = splitArrayIntoSubArrays(newArr, limit);
    const rows = getRowsToDisplay(pages, currentPage);

    // ActiveTotal used for pagination state
    setActiveTotal(newArr as SnippetUsage[]);
    // ReferencesByPage used to manage state of rows on each page
    setReferencesByPage(rows);
    // Adding timeout to ease transition
    setTimeout(() => setIsUpdating(false), STANDARD_TIME * 1000);
  };

  /**
   * Initializing Snippet Reference rows
   * 1. Determines if the rows are sorted
   * 2. Determines how many rows to show per page
   * 3. Create state for pagination
   */
  const initializeRows = () => {
    if (!isEmpty(results)) {
      const rowsWithResultType: SnippetUsage[] = addResultTypeToRow(results);
      // Snippet reference table will display either Template Name or Campaign Name if it exists
      // We need to mutate the data for sorting purposes.
      const modifiedResults: SnippetUsage[] = updateRowNames(rowsWithResultType);

      handleRowUpdates(modifiedResults);
      setAllReferences(modifiedResults);
    }
  };

  const modifyRows = () => {
    if (!isEmpty(allReferences)) {
      setIsUpdating(true);

      const filteredResults = allReferences.filter((status) => {
        const val = status.campaignState ? status.campaignState : InactiveState.Inactive;
        return selectedFilters.includes(val);
      });

      const updatedRows = isEmpty(selectedFilters) ? allReferences : filteredResults;
      handleRowUpdates(updatedRows);
    }
  };

  const handlePageChange = () => {
    if (!isEmpty(activeTotal)) {
      setIsUpdating(true);
      handleRowUpdates(activeTotal);
    }
  };

  useEffect(() => {
    initializeRows();
  }, [results]);

  useEffect(() => {
    modifyRows();
  }, [sortBy, sortDirection, selectedFilters]);

  useEffect(() => {
    handlePageChange();
  }, [currentPage, limit]);

  return {
    activeTotal,
    allReferences,
    referencesByPage,
    isLoading,
    isUpdating,
  };
}
